import axios from "redaxios"
import type { RouteLocationRaw } from "vue-router"
import { fetchCurrentUser } from "@/utils/ApiClient"
import { getDefaultPageRoute } from "@/utils/defaultPageRouting"

const authApiBase = () => import.meta.env.VITE_AUTH_API_ENDPOINT as string

/** Dashboard origin for session cookie sync (defaults to :3000 in dev). */
const dashboardBase = (): string | undefined => {
	const configured = import.meta.env.VITE_DASHBOARD_URL as string | undefined
	if (configured) return configured.replace(/\/$/, "")
	if (import.meta.env.DEV) return "http://localhost:3000"
	return undefined
}

type SessionSyncResult = {
	apiOk: boolean
	dashboardOk: boolean
}

function isDashboardRedirect(url: string | undefined): boolean {
	if (!url) return false

	try {
		const target = new URL(url)
		const dashboard = dashboardBase()
		if (dashboard) {
			return target.origin === new URL(dashboard).origin
		}
		return (target.hostname === "localhost" || target.hostname === "127.0.0.1") && target.port === "3000"
	} catch {
		return false
	}
}

/**
 * Persist JWT in httpOnly cookies for SSO.
 * - API cookie: shared across *.landerz.ca (staging/prod)
 * - Dashboard cookie: same origin as the dashboard (required for localhost:3000)
 */
export async function syncSessionCookie(apiToken: string): Promise<SessionSyncResult> {
	const result: SessionSyncResult = { apiOk: false, dashboardOk: false }

	if (!apiToken) return result

	const apiBase = authApiBase()
	const dashboard = dashboardBase()

	if (apiBase) {
		try {
			await axios.post(`${apiBase}/session`, { token: apiToken }, { withCredentials: true })
			result.apiOk = true
		} catch (error) {
			console.warn("API session cookie sync failed:", error)
		}
	}

	if (dashboard) {
		try {
			await axios.post(`${dashboard}/api/auth/session`, { token: apiToken }, { withCredentials: true })
			result.dashboardOk = true
		} catch (error) {
			console.warn("Dashboard session cookie sync failed:", error)
		}
	}

	return result
}

/** Clear session cookies on logout (API + dashboard origins). */
export async function clearSessionCookie(): Promise<void> {
	const apiBase = authApiBase()
	const dashboard = dashboardBase()

	if (apiBase) {
		try {
			await axios.delete(`${apiBase}/session`, { withCredentials: true })
		} catch (error) {
			console.warn("API session cookie clear failed:", error)
		}
	}

	if (dashboard) {
		try {
			await axios.delete(`${dashboard}/api/auth/session`, { withCredentials: true })
		} catch (error) {
			console.warn("Dashboard session cookie clear failed:", error)
		}
	}
}

/** Navigate after login — supports in-app paths and full dashboard URLs. */
function redirectAfterLogin(
	redirect: RouteLocationRaw | undefined,
	routerPush: (route: RouteLocationRaw) => void
): void {
	if (!redirect) {
		routerPush(getDefaultPageRoute(undefined))
		return
	}

	if (typeof redirect === "string" && (redirect.startsWith("http://") || redirect.startsWith("https://"))) {
		window.location.href = redirect
		return
	}

	routerPush(redirect)
}

async function resolveLoginRedirect(apiToken: string, redirectPath: string | undefined): Promise<RouteLocationRaw> {
	if (redirectPath) {
		return redirectPath
	}

	try {
		const currentUser = await fetchCurrentUser(apiToken, { includeProjects: false })

		return getDefaultPageRoute(currentUser.defaultPage)
	} catch (error) {
		console.warn("Could not load the default page:", error)

		return getDefaultPageRoute(undefined)
	}
}

/**
 * Sync session cookies, then redirect. For dashboard targets, requires the
 * dashboard cookie to be set first — otherwise returns an error (no redirect loop).
 */
export async function finishLoginAndRedirect(
	apiToken: string,
	redirectPath: string | undefined,
	routerPush: (route: RouteLocationRaw) => void
): Promise<{ ok: true } | { ok: false; error: string }> {
	const sync = await syncSessionCookie(apiToken)

	if (redirectPath && isDashboardRedirect(redirectPath) && !sync.dashboardOk) {
		return {
			ok: false,
			error:
				"Could not open the dashboard session. Ensure the dashboard is running (make dashboard.run), " +
				"LZ_AUTH_TOKEN_SECRET matches the API in dashboard/.env.local, and VITE_DASHBOARD_URL is set if not using localhost:3000.",
		}
	}

	const resolvedRedirect = await resolveLoginRedirect(apiToken, redirectPath)
	redirectAfterLogin(resolvedRedirect, routerPush)
	return { ok: true }
}
